import {useEffect, useState} from "react";

import {predictMenuDate} from "@apps/daily-menu/hooks/utils/predictMenuDate";
import {DailyMenuSearchParam} from "@apps/daily-menu/interfaces";
import {
  createSearchParams,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import {DateString} from "@globals/types/utilityTypes";
import {useUser} from "@globals/user";
import {checkValidDateStr} from "@globals/utils/toDateString";

export default function useSelectedMenuDate({
  menuDates,
}: {
  menuDates: DateString[];
}): {
  isDateAdjusted: boolean;
  latestError: string;
  selectedDate: DateString | undefined;
} {
  const {userHasChangedLocation, user} = useUser();
  const [isDateAdjusted, setIsDateAdjusted] = useState<boolean>(false);
  const [latestError, setLatestError] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<DateString>();

  const navigate = useNavigate();
  const {menuDay} = useParams();

  const [searchParams, setSearchParams] = useSearchParams();
  const dateParam = searchParams.get(DailyMenuSearchParam.Date);
  const nextSearchParams = new URLSearchParams(searchParams);

  const setNewDate = (newDate: DateString, mayBeAdjusted: boolean): void => {
    if (newDate !== selectedDate) {
      setSelectedDate(newDate);
      // Only set adjusted if user manually changed location
      setIsDateAdjusted(mayBeAdjusted && userHasChangedLocation);

      nextSearchParams.set(DailyMenuSearchParam.Date, newDate);
      setSearchParams(nextSearchParams);
    }
  };

  const setDateFromDateParamAndMenuDates = (validTargetDate: DateString) => {
    const dateIsAvailable = menuDates.includes(validTargetDate);
    const newDate = dateIsAvailable ? validTargetDate : menuDates[0];
    setNewDate(newDate, !dateIsAvailable);
  };

  const setDateFromDateParam = (targetDate: string) => {
    // Use newer ?date=yyyy-mm-dd query param
    if (!checkValidDateStr(targetDate)) {
      setLatestError("The date specified for this menu was not recognized.");
      return;
    }
    setDateFromDateParamAndMenuDates(targetDate);
  };

  const setDateFromMenuDay = (targetDate: DateString) => {
    // Set date based on Nth menu day, e.g. from old /n+1/ URL param
    nextSearchParams.set(DailyMenuSearchParam.Date, targetDate);
    navigate({
      pathname: "/",
      search: `?${createSearchParams(nextSearchParams)}`,
    });
  };

  const setKnownMenuDate = () => {
    if (dateParam) {
      setDateFromDateParam(dateParam);
    } else {
      const targetDate = menuDates[Number(menuDay ?? 1) - 1] ?? menuDates[0];
      setDateFromMenuDay(targetDate);
    }
  };

  const menuDatesAreAvailable = menuDates.length > 0;
  const shouldPredictMenuDate = Boolean(user?.uses_menu_date_prediction);

  useEffect(() => {
    if (menuDatesAreAvailable) {
      setKnownMenuDate();
    } else if (shouldPredictMenuDate) {
      const predictedMenuDate = predictMenuDate(dateParam, menuDay);
      if (predictedMenuDate === undefined) {
        setLatestError("The date specified for this menu was not recognized.");
        return;
      }
      setSelectedDate(predictedMenuDate);
    }
  }, [menuDates, menuDay, searchParams]);
  return {isDateAdjusted, latestError, selectedDate};
}
